import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Search, Siren } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill } from '@vnetwork/ui/data';
import type { ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Badge,
  Input,
  Skeleton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useAlarmEvents,
  useWatchpoints,
  useAcknowledgeIncident,
  useResolveIncident,
  type AlarmEvent,
  type AlarmEventStatus,
  type Watchpoint,
} from '@/api/healthcheck';
import { eventStatusTone, severityTone, fmtRelTime, typeMeta } from '../shared';
import { incidentSummary } from './incident-context';
import { IncidentDetailDialog } from './IncidentDetailDialog';
import { RowMenu } from '../../components/RowMenu';

const STATUS_FILTERS: { value: AlarmEventStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'triggered', label: 'Triggered' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'resolved', label: 'Resolved' },
];

const muted = (v: React.ReactNode) => (
  <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{v}</span>
);

export function IncidentList() {
  const { projectId } = useParams<{ projectId: string }>();
  const base = `/services/healthcheck/${projectId}`;
  const { show } = useToast();

  const [status, setStatus] = useState<AlarmEventStatus | 'all'>('triggered');
  const { data, isLoading, isError, refetch } = useAlarmEvents(
    status === 'all' ? { page_size: 100 } : { status, page_size: 100 },
  );
  // Alarm-events are tenant-wide; scope to this project's monitors via watchpoint_id.
  const { data: wp } = useWatchpoints(projectId);
  const monitorIds = useMemo(
    () => new Set((wp?.items ?? []).map((m) => m.id)),
    [wp],
  );
  const wpById = useMemo(
    () => new Map((wp?.items ?? []).map((m) => [m.id, m])),
    [wp],
  );
  const events = useMemo(
    () => (data?.items ?? []).filter((e) => monitorIds.has(e.watchpoint_id)),
    [data, monitorIds],
  );

  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const filteredEvents = q
    ? events.filter((e) => {
        const monitor = wpById.get(e.watchpoint_id);
        return (
          (monitor?.name ?? '').toLowerCase().includes(q) ||
          (e.alarm_name ?? '').toLowerCase().includes(q)
        );
      })
    : events;

  const ack = useAcknowledgeIncident();
  const resolve = useResolveIncident();
  const [selected, setSelected] = useState<AlarmEvent | null>(null);

  const ackIncident = (id: string) =>
    ack.mutate(id, {
      onSuccess: () => show({ tone: 'success', title: 'Acknowledged' }),
      onError: (e) => show({ tone: 'danger', title: (e as Error)?.message || 'Failed' }),
    });
  const resolveIncident = (id: string) =>
    resolve.mutate(id, {
      onSuccess: () => {
        show({ tone: 'success', title: 'Resolved' });
        setSelected(null);
      },
      onError: (e) => show({ tone: 'danger', title: (e as Error)?.message || 'Failed' }),
    });

  const columns = useMemo<ColumnDef<AlarmEvent, unknown>[]>(
    () => [
      {
        id: 'severity',
        header: 'Severity',
        enableSorting: false,
        cell: ({ row }) => {
          const sev = row.original.severity ?? 'warning';
          return (
            <Badge tone={severityTone(sev)} appearance="subtle" size="sm" style={{ textTransform: 'capitalize' }}>
              {sev}
            </Badge>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => (
          <StatusPill tone={eventStatusTone(row.original.status)}>
            {row.original.status}
          </StatusPill>
        ),
      },
      {
        id: 'monitor',
        header: 'Monitor',
        enableSorting: false,
        cell: ({ row }) => {
          const m = wpById.get(row.original.watchpoint_id);
          const tm = typeMeta(m?.type ?? 'http');
          const Icon = tm.icon;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', minWidth: 0 }}>
              <Icon size={15} style={{ flexShrink: 0, color: 'var(--vn-fg-muted)' }} />
              <span style={{ fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {m?.name ?? row.original.watchpoint_id.slice(0, 8)}
              </span>
              <Badge tone="neutral" appearance="subtle" size="sm">{tm.label}</Badge>
            </div>
          );
        },
      },
      {
        id: 'summary',
        header: 'Detail',
        enableSorting: false,
        cell: ({ row }) => {
          const m = wpById.get(row.original.watchpoint_id);
          return (
            <div style={{ minWidth: 0, maxWidth: 360 }}>
              {row.original.alarm_name && (
                <div style={{ fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {row.original.alarm_name}
                </div>
              )}
              <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {incidentSummary(m?.type ?? 'http', row.original.details, row.original.matched_items)}
              </div>
            </div>
          );
        },
      },
      {
        id: 'triggered',
        header: 'Triggered',
        enableSorting: false,
        cell: ({ row }) => muted(fmtRelTime(row.original.triggered_at)),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const ev = row.original;
          // Acknowledge applies only while triggered; Resolve until resolved.
          const items: { label: string; onSelect: () => void; danger?: boolean }[] = [];
          if (ev.status === 'triggered') items.push({ label: 'Acknowledge', onSelect: () => ackIncident(ev.id) });
          if (ev.status !== 'resolved') items.push({ label: 'Resolve', onSelect: () => resolveIncident(ev.id) });
          if (!items.length) return null;
          return <RowMenu items={items} />;
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ack, resolve, wpById],
  );

  return (
    <Container size="xl">
      <PageHeader
        icon={<Siren size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild><Link to={base}>Monitoring</Link></BreadcrumbItem>
            <BreadcrumbItem current>Incidents</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="Incidents"
        description="Alarm events raised by your monitors — acknowledge and resolve them here."
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Couldn't load incidents"
          actions={
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          The monitoring service is temporarily unavailable.
        </Alert>
      ) : isLoading ? (
        <Skeleton style={{ height: 240 }} />
      ) : (
        <>
          <Toolbar
            start={
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
                <Input
                  placeholder="Search by monitor or alarm…"
                  startSlot={<Search />}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  style={{ flex: '1 1 160px', maxWidth: 320 }}
                />
                <Select value={status} onValueChange={(v) => setStatus(v as AlarmEventStatus | 'all')}>
                  <SelectTrigger style={{ minWidth: 160, maxWidth: '100%' }}>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_FILTERS.map((f) => (
                      <SelectItem key={f.value} value={f.value}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            }
          />
          <DataTable
            columns={columns}
            data={filteredEvents}
            getRowId={(e) => e.id}
            onRowClick={(e) => setSelected(e)}
            emptyState={
              <EmptyState
                icon={<Siren size={28} />}
                title="No incidents"
                description={
                  q
                    ? 'No incidents match your search.'
                    : status === 'all'
                      ? 'No alarm events have been raised yet.'
                      : `No ${status} incidents.`
                }
              />
            }
            footer={filteredEvents.length ? `Showing ${filteredEvents.length} incident${filteredEvents.length === 1 ? '' : 's'}` : undefined}
          />
        </>
      )}

      <IncidentDetailDialog
        event={selected}
        watchpoint={selected ? (wpById.get(selected.watchpoint_id) as Watchpoint | undefined) : undefined}
        onClose={() => setSelected(null)}
        onAcknowledge={ackIncident}
        onResolve={resolveIncident}
        busy={ack.isPending || resolve.isPending}
      />
    </Container>
  );
}

